<template>
  <div class="flex flex-col min-h-0 h-full">
    <!-- Messages -->
    <div ref="scroller" class="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
      <template v-for="message in messages" :key="message.id">
        <p
          v-if="message.sender === 'system'"
          class="text-center text-xs text-gray-400 dark:text-gray-500"
        >
          {{ message.body }} · {{ formatTime(message.created_at) }}
        </p>

        <div v-else class="flex" :class="isOwn(message) ? 'justify-end' : 'justify-start'">
          <div class="max-w-[85%]">
            <p
              class="text-[11px] mb-0.5 text-gray-400 dark:text-gray-500"
              :class="isOwn(message) ? 'text-right' : 'text-left'"
            >
              {{ message.author }} · {{ formatTime(message.created_at) }}
            </p>
            <div
              class="px-3.5 py-2 rounded-2xl text-sm whitespace-pre-line break-words"
              :class="isOwn(message)
                ? 'bg-[var(--color-primary)] text-white rounded-br-md'
                : 'bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-gray-100 rounded-bl-md'"
            >{{ message.body }}</div>
          </div>
        </div>
      </template>

      <p v-if="!messages.length" class="text-center text-sm text-gray-400 py-8">
        Zatiaľ žiadne správy
      </p>
    </div>

    <slot name="notice" />

    <!-- Composer -->
    <form
      v-if="!readonly"
      class="border-t border-gray-200 dark:border-white/10 p-3 flex items-end gap-2"
      @submit.prevent="submit"
    >
      <textarea
        ref="input"
        v-model="text"
        rows="1"
        maxlength="2000"
        :placeholder="placeholder"
        class="flex-1 resize-none max-h-32 px-3 py-2 text-sm rounded-xl border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] dark:bg-white/5 dark:border-white/10 dark:text-gray-100"
        @keydown.enter.exact.prevent="submit"
        @input="autosize"
      />
      <button
        type="submit"
        :disabled="sending || !text.trim()"
        class="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white disabled:opacity-40 transition-colors"
        aria-label="Odoslať"
      >
        <Loader2 v-if="sending" class="w-4 h-4 animate-spin" />
        <SendHorizontal v-else class="w-4 h-4" />
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue'
import { SendHorizontal, Loader2 } from 'lucide-vue-next'

const props = defineProps({
  messages: { type: Array, default: () => [] },
  // whose messages are drawn on the right: the customer's or the staff's
  perspective: { type: String, default: 'customer' },
  sending: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Napíšte správu…' },
})

const emit = defineEmits(['send'])

const text = ref('')
const scroller = ref(null)
const input = ref(null)

const isOwn = (message) => message.sender === props.perspective

const formatTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  const today = new Date()
  const time = date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
  return date.toDateString() === today.toDateString()
    ? time
    : `${date.toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric' })} ${time}`
}

const scrollToBottom = async () => {
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
}

const autosize = () => {
  if (!input.value) return
  input.value.style.height = 'auto'
  input.value.style.height = `${input.value.scrollHeight}px`
}

const submit = () => {
  const body = text.value.trim()
  if (!body || props.sending) return
  emit('send', body, () => {
    text.value = ''
    nextTick(autosize)
  })
}

watch(() => props.messages.length, scrollToBottom)
onMounted(scrollToBottom)

defineExpose({ focus: () => input.value?.focus() })
</script>
